// src/pages/Onboarding.jsx - Business Onboarding Wizard
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Building2, Users, MapPin, Laptop, ArrowRight, ArrowLeft, CheckCircle } from 'lucide-react';
import { API_ENDPOINTS } from '../config/api';
import { useAuth } from '../contexts/AuthContext';

// Import UI components
import { Button } from '../components/ui/button';
import { Spinner } from '../components/ui/spinner';
import { Alert, AlertDescription } from '../components/ui/alert';

const Onboarding = () => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    business_type: '',
    team_size: '',
    has_physical_location: null,
    current_software: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const totalSteps = 4;
  const progress = (step / totalSteps) * 100;

  const businessTypes = [
    { value: 'service_business', label: 'Service Business', description: 'Field services, home services, repairs' },
    { value: 'consulting', label: 'Consulting', description: 'Professional consulting services' },
    { value: 'retail', label: 'Retail', description: 'Physical or online retail' },
    { value: 'other', label: 'Other', description: 'Something else' }
  ];

  const teamSizes = [
    { value: 'solo', label: 'Solo', description: 'Just me' },
    { value: '2-5', label: '2-5 people', description: 'Small team' },
    { value: '6-20', label: '6-20 people', description: 'Growing team' },
    { value: '20+', label: '20+ people', description: 'Established team' }
  ];

  const handleNext = () => {
    if (step < totalSteps) {
      setStep(step + 1);
    } else {
      handleComplete();
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleComplete = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(
        `${API_ENDPOINTS.businessAuth.replace('/auth', '')}/complete-onboarding`,
        formData
      );

      if (response.data.status === 'success') {
        console.log('✅ Onboarding completed successfully');

        // Refresh user data to get updated onboarding_completed status
        await refreshUser();

        navigate('/dashboard');
      }
    } catch (err) {
      console.error('❌ Onboarding failed:', err);
      setError(err.response?.data?.message || 'Failed to complete onboarding');
    } finally {
      setLoading(false);
    }
  };

  const isStepValid = () => {
    switch (step) {
      case 1:
        return formData.business_type !== '';
      case 2:
        return formData.team_size !== '';
      case 3:
        return formData.has_physical_location !== null;
      case 4:
        return true; // Current software is optional
      default:
        return false;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center p-6">
      <div className="w-full max-w-2xl">
        {/* Card */}
        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl shadow-2xl p-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-white mb-2">Let's set up your business</h1>
            <p className="text-slate-400">This will help us personalize your experience</p>
          </div>

          {/* Progress Bar */}
          <div className="mb-8">
            <div className="flex justify-between text-sm text-slate-400 mb-2">
              <span>Step {step} of {totalSteps}</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <div className="w-full bg-slate-700 rounded-full h-2">
              <div
                className="bg-emerald-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <Alert variant="destructive" className="mb-6">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Step 1: Business Type */}
          {step === 1 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-4">
                <Building2 className="w-8 h-8 text-emerald-400" />
                <h2 className="text-2xl font-semibold text-white">What type of business do you run?</h2>
              </div>

              <div className="grid grid-cols-1 gap-3">
                {businessTypes.map((type) => (
                  <button
                    key={type.value}
                    onClick={() => setFormData({ ...formData, business_type: type.value })}
                    className={`p-4 rounded-lg border-2 text-left transition-all ${
                      formData.business_type === type.value
                        ? 'border-emerald-500 bg-emerald-500/10'
                        : 'border-slate-600 bg-slate-700/30 hover:border-slate-500'
                    }`}
                  >
                    <div className="font-semibold text-white">{type.label}</div>
                    <div className="text-sm text-slate-400">{type.description}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Team Size */}
          {step === 2 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-4">
                <Users className="w-8 h-8 text-emerald-400" />
                <h2 className="text-2xl font-semibold text-white">How big is your team?</h2>
              </div>

              <div className="grid grid-cols-1 gap-3">
                {teamSizes.map((size) => (
                  <button
                    key={size.value}
                    onClick={() => setFormData({ ...formData, team_size: size.value })}
                    className={`p-4 rounded-lg border-2 text-left transition-all ${
                      formData.team_size === size.value
                        ? 'border-emerald-500 bg-emerald-500/10'
                        : 'border-slate-600 bg-slate-700/30 hover:border-slate-500'
                    }`}
                  >
                    <div className="font-semibold text-white">{size.label}</div>
                    <div className="text-sm text-slate-400">{size.description}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 3: Physical Location */}
          {step === 3 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-4">
                <MapPin className="w-8 h-8 text-emerald-400" />
                <h2 className="text-2xl font-semibold text-white">Do you have a physical location?</h2>
              </div>

              <div className="grid grid-cols-1 gap-3">
                <button
                  onClick={() => setFormData({ ...formData, has_physical_location: true })}
                  className={`p-4 rounded-lg border-2 text-left transition-all ${
                    formData.has_physical_location === true
                      ? 'border-emerald-500 bg-emerald-500/10'
                      : 'border-slate-600 bg-slate-700/30 hover:border-slate-500'
                  }`}
                >
                  <div className="font-semibold text-white">Yes, I have a physical location</div>
                  <div className="text-sm text-slate-400">Office, store, or workspace</div>
                </button>

                <button
                  onClick={() => setFormData({ ...formData, has_physical_location: false })}
                  className={`p-4 rounded-lg border-2 text-left transition-all ${
                    formData.has_physical_location === false
                      ? 'border-emerald-500 bg-emerald-500/10'
                      : 'border-slate-600 bg-slate-700/30 hover:border-slate-500'
                  }`}
                >
                  <div className="font-semibold text-white">No, I'm mobile or remote</div>
                  <div className="text-sm text-slate-400">Field service or home-based</div>
                </button>
              </div>
            </div>
          )}

          {/* Step 4: Current Software */}
          {step === 4 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-4">
                <Laptop className="w-8 h-8 text-emerald-400" />
                <h2 className="text-2xl font-semibold text-white">What software do you currently use?</h2>
              </div>

              <div className="space-y-3">
                <p className="text-slate-400">This helps us provide better recommendations (optional)</p>
                <textarea
                  value={formData.current_software}
                  onChange={(e) => setFormData({ ...formData, current_software: e.target.value })}
                  placeholder="e.g., Excel, Google Calendar, QuickBooks..."
                  className="w-full p-4 bg-slate-700/30 border-2 border-slate-600 rounded-lg text-white placeholder-slate-500 focus:border-emerald-500 focus:outline-none resize-none"
                  rows={4}
                />
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8">
            <Button
              variant="secondary"
              size="default"
              onClick={handleBack}
              disabled={step === 1 || loading}
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>

            <Button
              size="default"
              onClick={handleNext}
              disabled={loading || !isStepValid()}
            >
              {loading ? (
                <>
                  <Spinner />
                  Saving...
                </>
              ) : (
                <>
                  {step === totalSteps ? 'Complete' : 'Next'}
                  {step === totalSteps ? <CheckCircle className="w-4 h-4" /> : <ArrowRight className="w-4 h-4" />}
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
